/**
 * Language Context - Multi-language Support for POS System
 * 
 * Last Updated: 2025-01-27
 * 
 * Today's Changes (2025-01-27):
 * ==============================
 * 
 * 1. Login Page Improvements:
 *    - Fixed logo grey background removal
 *    - Added background image support from Firebase Storage
 *    - Updated login page styling to match professional design
 * 
 * 2. Sidebar Navigation Enhancements:
 *    - Reduced collapse/expand button size from 52x52px to 40x40px
 *    - Moved button from left side to right side of sidebar
 *    - Updated chevron icon size from 30px to 22px
 *    - Improved button positioning and animations
 * 
 * 3. Split Table Dialog Fixes:
 *    - Replaced browser prompt() with proper Material-UI input form
 *    - Added inline input field for adding person names
 *    - Implemented proper form validation and error handling
 *    - Added keyboard shortcuts (Enter to submit, Escape to cancel)
 *    - Enhanced user experience with professional input design
 * 
 * 4. Translation Keys Added:
 *    - split.add: "Add" / "Hinzufügen" / "Toevoegen"
 *    - split.cancel: "Cancel" / "Abbrechen" / "Annuleren"
 *    - split.error_person_name_required: Error message for empty person name
 *    - split.max_people_reached: Message when 8 people limit is reached
 * 
 * 5. Code Improvements:
 *    - Better state management for add person functionality
 *    - Proper form reset when dialog opens/closes
 *    - Enhanced validation and user feedback
 *    - Consistent styling with login page design
 * 
 * Supported Languages:
 * - English (en) - Default for admin/staff
 * - German (de) - Default for client (as per requirements)
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation keys
const translations = {
  en: {
    // Menu
    'menu.sold_out_badge': 'SOLD OUT',
    'menu.sold_out': 'Sold out',
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.restaurant': 'Restaurant',
    'nav.orders': 'Orders',
    'nav.delivery': 'Delivery',
    'nav.tables': 'Tables',
    'nav.kitchen': 'Kitchen',
    'nav.drivers': 'Drivers',
    'nav.backoffice': 'Backoffice',
    'nav.settings': 'Settings',
    'nav.payments': 'Payments',
    'nav.reports': 'Reports',
    'nav.daily_closing': 'Daily Closing',
    'nav.print_preview': 'Print Preview',
    'nav.logout': 'Logout',
    
    // Dashboard
    'dashboard.new_order': 'Create New Order',
    'dashboard.select_items': 'Browse our menu and add items to create the perfect order',
    // (Generic copy; client demos can localize per-brand)
    'dashboard.search_placeholder': 'Search menu items...',
    'dashboard.categories': 'Categories',
    'dashboard.clear': 'Clear',
    'dashboard.found_items': 'Found {count} item(s) for "{query}"',
    'dashboard.add_to_cart': 'Add to Cart',
    'dashboard.current_order': 'Current Order',
    'dashboard.items_count': 'items',
    'dashboard.cart_empty': 'Your cart is empty',
    'dashboard.add_items_start': 'Add items from the menu to get started',
    'dashboard.subtotal': 'Subtotal',
    'dashboard.tax': 'Tax ({rate}%)',
    'dashboard.total': 'Total',
    'dashboard.apply_discount': 'Apply Discount',
    'dashboard.change_discount': 'Change Discount',
    'dashboard.discount_title': 'Apply Discount',
    'dashboard.discount_percentage': 'Percentage',
    'dashboard.discount_fixed': 'Fixed Amount',
    'dashboard.discount_value_pct': 'Discount in %',
    'dashboard.discount_value_eur': 'Discount in €',
    'dashboard.discount_reason': 'Reason (optional)',
    'dashboard.discount_reason_placeholder': 'e.g. Regular customer, Employee...',
    'dashboard.discount_original': 'Original',
    'dashboard.discount_amount': 'Discount',
    'dashboard.discount_new_price': 'New Price',
    'dashboard.discount_cancel': 'Cancel',
    'dashboard.discount_apply': 'Apply Discount',
    'dashboard.discount_remove': 'Remove Discount',
    'dashboard.checkout': 'Checkout ({count} items)',
    'dashboard.split_table': 'Split Table ({count} items)',
    'dashboard.clear_cart': 'Clear Cart',
    'dashboard.refresh_theme': 'Refresh Theme',
    
    // Checkout Dialog
    'checkout.title': 'Checkout',
    'checkout.customer_info': 'Customer Information',
    'checkout.customer_data': 'Customer Data',
    'checkout.customer_name': 'Customer Name',
    'checkout.first_name': 'First Name',
    'checkout.last_name': 'Last Name',
    'checkout.customer_phone': 'Phone Number',
    'checkout.address': 'Address',
    'checkout.postcode': 'Postal Code',
    'checkout.postcode_helper': 'Enter postal code for address suggestions',
    'checkout.city': 'City',
    'checkout.customer_name_helper': 'Leave empty for walk-in customers. Start typing for suggestions.',
    'checkout.order_type': 'Order Type',
    'checkout.dine_in': 'Dine In',
    'checkout.takeaway': 'Takeaway',
    'checkout.delivery': 'Delivery',
    'checkout.table_number': 'Table Number (Optional)',
    'checkout.payment_method': 'Payment Method',
    'checkout.cash': 'Cash',
    'checkout.card': 'Card',
    'checkout.paypal': 'PayPal',
    'checkout.split_payment': 'Split Payment',
    'checkout.order_summary': 'Order Summary',
    'checkout.subtotal': 'Subtotal',
    'checkout.tax_7': 'VAT (7%)',
    'checkout.tax_19': 'VAT (19%)',
    'checkout.tax': 'VAT',
    'checkout.total': 'Total',
    'checkout.processing': 'Processing...',
    'checkout.complete_order': 'Complete Order',
    'checkout.required_fields_missing': 'Required Fields Missing',
    'checkout.please_fill_required': 'Please fill in the following fields to complete your delivery order:',
    'checkout.notes': 'Special Instructions',
    'checkout.cancel': 'Cancel',
    'checkout.proceed': 'Proceed to Payment',
    'checkout.next': 'Next',
    'checkout.leave_empty_walkin': 'Leave empty for walk-in customers',
    'checkout.back': 'Back',
    'checkout.confirm_order': 'Confirm Order',
    
    // Payment Dialog
    'payment.payment_details': 'Payment Details',
    'payment.amount': 'Amount',
    'payment.cash_received': 'Cash Received',
    'payment.change': 'Change',
    'payment.pay_now': 'Pay Now',
    
    // Success Dialog
    'success.order_complete': 'Order Complete!',
    'success.order_number': 'Order Number',
    'success.customer': 'Customer',
    'success.total': 'Total',
    'success.payment': 'Payment',
    'success.sent_to_kitchen': 'Order has been sent to kitchen and admin panel',
    'success.print_receipt': 'Print Receipt',
    'success.print_kitchen': 'Print Kitchen Order',
    'success.new_order': 'New Order',
    'success.print_receipt_question': 'Print Receipt?',
    'success.print_receipt_message': 'Would you like to print a receipt for this order?',
    'success.no_skip': 'No, Skip',
    'success.preview': 'Preview',
    'success.yes_print': 'Yes, Print',
    
    // Orders Page
    'orders.title': 'Orders',
    'orders.order_id': 'Order ID',
    'orders.customer': 'Customer',
    'orders.items': 'Items',
    'orders.total': 'Total',
    'orders.discount': 'Discount',
    'orders.apply_discount': 'Apply Discount',
    'orders.change_discount': 'Change Discount',
    'orders.discount_dialog_title': 'Apply Discount to Order',
    'orders.discount_type': 'Type',
    'orders.discount_value': 'Value',
    'orders.discount_reason': 'Reason (optional)',
    'orders.discount_percentage': 'Percentage',
    'orders.discount_fixed': 'Fixed Amount',
    'orders.remove_discount': 'Remove Discount',
    'orders.status': 'Status',
    'orders.date': 'Date',
    'orders.time': 'Time',
    'orders.actions': 'Actions',
    'orders.view': 'View',
    'orders.assign_driver': 'Assign Driver',
    'orders.print': 'Print',
    'orders.start_preparing': 'Start Preparing',
    'orders.mark_ready': 'Mark Ready',
    'orders.print_order': 'Print Order',
    'orders.assign_delivery_driver': 'Assign Delivery Driver',
    'orders.available': 'Available',
    'orders.selected': 'Selected',
    'orders.assign_driver_btn': 'Assign Driver',
    'orders.cancel': 'Cancel',
    'orders.order_details': 'Order Details',
    'orders.items_count': 'items',
    'orders.status_pending': 'Pending',
    'orders.status_confirmed': 'Confirmed',
    'orders.status_preparing': 'Preparing',
    'orders.status_ready': 'Ready',
    'orders.status_delivered': 'Delivered',
    'orders.customer_info': 'Customer Information',
    'orders.order': 'Order',
    'orders.walk_in_customer': 'Walk-in Customer',
    'orders.no_items_found': 'No items found',
    'orders.thank_you': 'Thank you for your order!',
    'orders.print_preview': 'Print Preview',
    'orders.print_receipt': 'Print Receipt',
    'orders.close_preview': 'Close Preview',
    'orders.menu_item': 'Menu Item',
    'payments.status_completed': 'Completed',
    'payments.status_pending': 'Pending',
    'payments.status_failed': 'Failed',
    'payments.status_cancelled': 'Cancelled',
    'payments.status_refunded': 'Refunded',
    'orders.delivery_customer': 'Delivery Customer',
    'orders.order_summary': 'Order Summary',
    'orders.order_items': 'Order Items',
    'orders.no_name_entered': 'No name entered',
    'orders.refresh_orders': 'Refresh Orders',
    'orders.customer_information': 'Customer Information',
    'orders.close': 'Close',
    'orders.select_driver_for_order': 'Select a driver for order',
    
    // Reports Page
    'reports.title': 'Sales Reports & Analytics',
    'reports.subtitle': 'Comprehensive sales analytics and performance insights - Auto-refreshing every 30 seconds',
    'reports.total_revenue': 'Total Revenue',
    'reports.total_orders': 'Total Orders',
    'reports.average_order': 'Average Order Value',
    'reports.total_customers': 'Total Customers',
    'reports.revenue_by_day': 'Revenue by Day',
    'reports.orders_by_hour': 'Orders by Hour',
    'reports.top_items': 'Top Items',
    'reports.sales_performance': 'Sales Performance',
    'reports.revenue_trend': 'Revenue Trend',
    'reports.order_distribution': 'Order Distribution',
    'reports.item_performance': 'Item Performance',
    'reports.refresh_data': 'Refresh Data',
    'reports.export_data': 'Export Data',
    'reports.print_report': 'Print Report',
    'reports.average_order_value': 'Average Order Value',
    'reports.payment_methods': 'Payment Methods',
    'reports.top_selling_items': 'Top Selling Items',
    'reports.sales_by_hour': 'Sales by Hour',
    'reports.filter': 'Filter',
    'reports.export': 'Export',
    'reports.refresh': 'Refresh',
    'reports.filter_options': 'Filter Options',
    'reports.date_range': 'Date Range',
    'reports.last_7_days': 'Last 7 Days',
    'reports.last_30_days': 'Last 30 Days',
    'reports.last_90_days': 'Last 90 Days',
    'reports.custom_range': 'Custom Range',
    'reports.order_status': 'Order Status',
    'reports.all_statuses': 'All Statuses',
    'reports.payment_method': 'Payment Method',
    'reports.all_methods': 'All Methods',
    'reports.branch': 'Branch',
    'reports.all_branches': 'All Branches',
    'reports.start_date': 'Start Date',
    'reports.end_date': 'End Date',
    'reports.apply_filters': 'Apply Filters',
    'reports.export_report': 'Export Report',
    'reports.export_format': 'Export Format',
    'reports.report_language': 'Report Language',
    'reports.tse_compliance_note': 'This report is TSE compliant and includes all required German tax information.',
    'reports.generate_export': 'Generate Export',
    
    // Payments Page
    'payments.title': 'Payment Management',
    'payments.subtitle': 'Manage transactions, refunds, and payment methods',
    'payments.transaction_history': 'Transaction History',
    'payments.payment_methods': 'Payment Methods',
    'payments.recent_transactions': 'Recent Transactions',
    'payments.filter_by': 'Filter by',
    'payments.export': 'Export',
    'payments.print': 'Print',
    'payments.refund': 'Refund',
    'payments.process': 'Process',
    'payments.amount': 'Amount',
    'payments.date': 'Date',
    'payments.method': 'Method',
    'payments.customer': 'Customer',
    'payments.actions': 'Actions',
    'payments.process_refund': 'Process Refund',
    'payments.refund_for_payment': 'Refund for payment',
    'payments.refund_amount': 'Refund Amount',
    'payments.refund_reason': 'Refund Reason',
    'payments.print_receipt': 'Print Receipt',
    'payments.cancel': 'Cancel',
    'payments.filter': 'Filter',
    'payments.refresh': 'Refresh',
    'payments.filter_options': 'Filter Options',
    'payments.date_range': 'Date Range',
    'payments.last_7_days': 'Last 7 Days',
    'payments.last_30_days': 'Last 30 Days',
    'payments.last_90_days': 'Last 90 Days',
    'payments.custom_range': 'Custom Range',
    'payments.payment_status': 'Payment Status',
    'payments.all_statuses': 'All Statuses',
    'payments.payment_method': 'Payment Method',
    'payments.all_methods': 'All Methods',
    'payments.amount_range': 'Amount Range',
    'payments.all_amounts': 'All Amounts',
    'payments.start_date': 'Start Date',
    'payments.end_date': 'End Date',
    'payments.apply_filters': 'Apply Filters',
    'payments.export_report': 'Export Report',
    'payments.export_format': 'Export Format',
    'payments.report_language': 'Report Language',
    'payments.tse_compliance_note': 'This report is TSE compliant and includes all required German tax information.',
    'payments.generate_export': 'Generate Export',
    'payments.cash': 'Cash',
    'payments.card': 'Card',
    'payments.paypal': 'PayPal',
    'payments.payment_id': 'Payment ID',
    'payments.order_id': 'Order ID',
    'payments.order': 'Order',
    'payments.transaction_id': 'Transaction ID',
    'payments.payment_information': 'Payment Information',
    'payments.payment_details': 'Payment Details',
    'payments.transaction': 'Transaction',
    'payments.close': 'Close',
    'payments.total_revenue': 'Total Revenue',
    'payments.total_transactions': 'Total Transactions',
    'payments.average_transaction': 'Average Transaction',
    
    // Status
    'status.pending': 'Pending',
    'status.confirmed': 'Confirmed',
    'status.preparing': 'Preparing',
    'status.ready': 'Ready',
    'status.delivered': 'Delivered',
    'status.cancelled': 'Cancelled',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.close': 'Close',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.not_available': 'Not Available',
    'common.not_provided': 'Not provided',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.cancel': 'Cancel',
    'common.optional': 'Optional',

    // Split Table Dialog
    'split.title': 'Split Table - Table',
    'split.step_create': 'Create Split',
    'split.step_assign': 'Assign Items',
    'split.step_payment': 'Process Payments',
    'split.step_complete': 'Complete',
    'split.create_for_table': 'Create split for table',
    'split.original_total': 'Original Order Total',
    'split.add_person': 'Add Person',
    'split.add': 'Add',
    'split.cancel': 'Cancel',
    'split.person_name': 'Person Name',
    'split.error_person_name_required': 'Please enter a person name',
    'split.max_people_reached': 'Maximum 8 people allowed',
    'split.items': 'Items',
    'split.total': 'Total',
    'split.complete': 'Complete Split',
    'split.error_no_person': 'Please add at least one person to split the table.',
    'split.error_no_items': 'Please assign at least one item to a person before proceeding.',
    'split.error_no_payment': 'Please select payment methods for all persons before proceeding.',
    'split.success': 'Table split completed successfully!',
    'split.orders_sent': 'Orders have been sent to Orders page',
    'split.kitchen_printed': 'Kitchen order printed',
    'split.receipt_printed': 'Receipt printed',
    'split.assign_items_title': 'Assign Items to Persons',
    'split.available_items': 'Available Items',
    'split.assign_persons': 'Assign Persons',
    'split.items_assigned': '✅ Items successfully assigned',
    'split.assign_selected_items': 'Assign {count} selected item{plural}',
    'split.no_items_selected': 'No items selected',
    'split.process_payments_title': 'Process Payments - Individual Amounts & Payment Methods',
    'split.process_payments_desc': 'Staff can set individual amounts for each person and choose separate payment methods',
    'split.from_assigned_items': 'From assigned items:',
    'split.subtotal': 'Subtotal',
    'split.vat_19': 'VAT (19%)',
    'split.items_total': 'Items Total',
    'split.set_custom_amount': 'Set Custom Amount',
    'split.custom_amount': 'Custom Amount (€)',
    'split.original_amount': 'Original amount',
    'split.final_amount': 'Final Amount',
    'split.payment_method': 'Payment Method',
    'split.add_tip': 'Add Tip',
    'split.tip_amount': 'Tip Amount (€)',
    'split.final_total_tip': 'Final Total (including tip)',
    'split.process_payment': 'Process Payment',
    'split.paid': 'Paid',
    'split.complete_title': 'Complete Split Table',
    'split.complete_success': '✅ Split table completed successfully! All payments processed.',
    'split.table': 'Table',
    'split.number_of_people': 'Number of People',
    'split.total_amount': 'Total Amount',
    'split.avg_per_person': 'Average per Person',
    
    // Order Details
    'order_details.customer_info': 'Customer Information',
    'order_details.first_name': 'First Name',
    'order_details.last_name': 'Last Name',
    'order_details.customer_name': 'Customer Name',
    'order_details.telephone': 'Telephone Number',
    'order_details.address': 'Address',
    'order_details.postcode': 'Postcode',
    'order_details.city': 'City',
    'order_details.delivery_address': 'Delivery Address',
    'order_details.order_type': 'Order Type',
    'order_details.dine_in': 'Dine-in Order',
    'order_details.takeaway': 'Takeaway Order',
    'order_details.delivery': 'Delivery Order',
    'order_details.order_items': 'Order Items',
    'order_details.no_items': 'No items found in this order',
    'order_details.print_preview': 'Print Preview',
    'order_details.driver_assigned': 'Driver Assigned - Status Managed by Rider',
    'order_details.assign_driver': 'Assign Delivery Driver',
    'order_details.driver': 'Driver',
    'order_details.loading_drivers': 'Loading drivers...',
    'order_details.no_drivers': 'No drivers available. Please add drivers in Admin Panel → Drivers Management.',
    'order_details.available_drivers': 'Available Drivers',
    'order_details.busy_offline': 'Busy/Offline',
    'order_details.busy': 'Busy',
    'order_details.offline': 'Offline',
    'order_details.available': 'Available',
    
    // Daily Closing Reports
    'daily_closing.title': 'Daily Closing Reports & End-of-Day Summary',
    'daily_closing.generate_report': 'Generate Daily Report',
    'daily_closing.refresh': 'Refresh',
    'daily_closing.back_dashboard': 'Back to Dashboard',
    'daily_closing.interim_summary': 'Interim Sales Summary',
    'daily_closing.interim_description': 'Generate real-time sales summary for current period',
    'daily_closing.generate_interim': 'Generate Interim Summary',
    'daily_closing.print_reports': 'Print Reports',
    'daily_closing.print_description': 'Print daily closing reports to physical printers',
    'daily_closing.print_latest': 'Print Latest Report',
    'daily_closing.export_reports': 'Export Reports',
    'daily_closing.export_description': 'Export reports to PDF or Excel format',
    'daily_closing.pdf': 'PDF',
    'daily_closing.excel': 'Excel',
    'daily_closing.history': 'Daily Closing Reports History',
    'daily_closing.date': 'Date',
    'daily_closing.cashier': 'Cashier',
    'daily_closing.total_sales': 'Total Sales',
    'daily_closing.orders': 'Orders',
    'daily_closing.cash_difference': 'Cash Difference',
    'daily_closing.status': 'Status',
    'daily_closing.actions': 'Actions',
    'daily_closing.draft': 'Draft',
    'daily_closing.completed': 'Completed',
    'daily_closing.no_reports': 'No reports available to print',
    'daily_closing.generate_report_title': 'Generate Daily Closing Report',
    'daily_closing.report_date': 'Report Date',
    'daily_closing.cashier_name': 'Cashier Name',
    'daily_closing.opening_cash': 'Opening Cash (EUR)',
    'daily_closing.actual_cash': 'Actual Cash Count (EUR)',
    'daily_closing.generate': 'Generate Report',
    'daily_closing.financial_summary': 'Financial Summary',
    'daily_closing.total_customers': 'Total Customers',
    'daily_closing.average_order_value': 'Average Order Value',
    'daily_closing.cash_reconciliation': 'Cash Reconciliation',
    'daily_closing.opening_cash_label': 'Opening Cash',
    'daily_closing.cash_sales': 'Cash Sales',
    'daily_closing.expected_cash': 'Expected Cash',
    'daily_closing.actual_cash_label': 'Actual Cash',
    'daily_closing.top_selling_items': 'Top Selling Items',
    'daily_closing.hourly_sales': 'Hourly Sales Breakdown',
    'daily_closing.print_report': 'Print Report',
    'daily_closing.print_preview': 'Print Preview',
    'daily_closing.no_reports_found': 'No daily closing reports found. Generate your first report to get started.',
    'daily_closing.reset_title': 'Reset Daily Closing',
    'daily_closing.reset_description': 'This process logs the reset for administration and deletes the existing daily closing report. Afterwards, you can create a new report for this date with corrected values.',
    'daily_closing.date_cashier': 'Date / Cashier',
    'daily_closing.reset_reason': 'Reason for Reset (Required)',
    'daily_closing.reset_reason_placeholder': 'e.g. Incorrect cash balance, test report, wrong employee, etc.',
    'daily_closing.reset_save': 'Save Reset with Reason',
    'daily_closing.reset_reason_required': 'Please enter a reason for the reset.',
    'daily_closing.reset_success': 'Daily closing report has been reset. Please create a new report for this date.',
    'daily_closing.reset_error': 'Error resetting report. Please check console for details.',
    'daily_closing.print_server_not_running': 'Print server is not running. Please start the local print server.',
    'daily_closing.print_success': 'Daily closing report printed successfully!',
    'daily_closing.print_error': 'Error printing report',
    'daily_closing.delete_confirm': 'Are you sure you want to delete this report? This action cannot be undone.',
    'daily_closing.delete_success': 'Report deleted successfully!',
    'daily_closing.delete_error': 'Failed to delete report. Please check console for details.',
    'daily_closing.view_details': 'View Details',
    'daily_closing.export_pdf': 'Export PDF',
    'daily_closing.h_difference': 'Cash Difference',
    'daily_closing.branch': 'Branch',
    'daily_closing.shift': 'Shift',
    'daily_closing.gross_revenue': 'Gross Revenue',
    'daily_closing.net_revenue': 'Net Revenue',
    'daily_closing.tax_collected': 'VAT (19%)',
    'daily_closing.discounts_given': 'Discounts Given',
    'daily_closing.refunds_issued': 'Refunds Issued',
    'daily_closing.split_payments': 'Split Payments',
    'daily_closing.split_payment_orders': 'Number of Split Orders',
    'daily_closing.split_payment_revenue': 'Revenue from Split Payments',
    'daily_closing.split_payment_percentage': 'Split Payment Percentage',
    'daily_closing.payment_methods': 'Payment Methods',
    'daily_closing.cash': 'Cash',
    'daily_closing.card': 'Card',
    'daily_closing.paypal': 'PayPal',
    'daily_closing.other': 'Other',
    'daily_closing.cash_refunds': 'Cash Refunds',
    'daily_closing.cash_over_short': 'Cash Over/Short',
    'daily_closing.top_selling_items_title': 'Top Selling Items',
    'daily_closing.operational_metrics': 'Operational Metrics',
    'daily_closing.peak_hours': 'Peak Hours',
    'daily_closing.slow_hours': 'Slow Hours',
    'daily_closing.average_service_time': 'Average Service Time',
    'daily_closing.customer_satisfaction': 'Customer Satisfaction',
    'daily_closing.audit_trail': 'Audit Trail',
    'daily_closing.report_generated_at': 'Report Generated',
    'daily_closing.generated_by': 'Generated By',
    'daily_closing.first_order_time': 'First Order',
    'daily_closing.last_order_time': 'Last Order',
    'daily_closing.system_uptime': 'System Uptime',
    'daily_closing.errors_logged': 'Errors Logged',
    'daily_closing.report_end': 'END OF DAILY CLOSING REPORT',
    'daily_closing.interim_success': 'Interim Sales Summary Generated Successfully!',
    'daily_closing.current_sales': 'Current Sales',
    'daily_closing.current_orders': 'Current Orders',
    'daily_closing.hourly_rate': 'Hourly Rate',
    'daily_closing.projected_daily_sales': 'Projected Daily Sales',
    'daily_closing.summary_saved': 'Summary saved to database.',
    'daily_closing.interim_error': 'Failed to generate interim sales summary. Please check console for details.',
    'daily_closing.unknown_staff': 'Unknown Staff Member',

    // Restaurant/Dine-in translations
    'restaurant.title': 'Restaurant Management',
    'restaurant.subtitle': 'Master your dining experience with real-time table management, seamless reservations, and instant kitchen coordination',
    'restaurant.table_management': 'Table Management',
    'restaurant.seating_area': 'Seating Area',
    'restaurant.indoor': 'Indoor',
    'restaurant.outdoor': 'Outdoor',
    'restaurant.table_status': 'Table Status',
    'restaurant.available': 'Available',
    'restaurant.occupied': 'Occupied',
    'restaurant.reserved': 'Reserved',
    'restaurant.cleaning': 'Cleaning',
    'restaurant.quick_actions': 'Quick Actions',
    'restaurant.print_all': 'Print All',
    'restaurant.kitchen_view': 'Kitchen View',
    'restaurant.add_table': 'Add Table',
    'restaurant.new_order': 'New Order',
    'restaurant.draft_orders': 'Draft Split Orders',
    'restaurant.table': 'Table',
    'restaurant.capacity': 'Capacity',
    'restaurant.people': 'people',
    'restaurant.items': 'items',
    'restaurant.walk_in_customer': 'Walk-in Customer',
    'restaurant.status_available': 'Available',
    'restaurant.status_occupied': 'Occupied',
    'restaurant.status_reserved': 'Reserved',
    'restaurant.status_cleaning': 'Cleaning',
    'restaurant.order': 'Order',
    'restaurant.order_items': 'Order Items',
    'restaurant.no_items': 'No items in order',
    'restaurant.total': 'Total',
    'restaurant.menu_items': 'Menu Items',
    'restaurant.print_kitchen': 'Print Kitchen',
    'restaurant.print_check': 'Print Check',
    'restaurant.prebill_generating': 'Generating pre-bill...',
    'restaurant.prebill_no_items': 'No open items on this table.',
    'restaurant.prebill_printed': 'Pre-bill printed',
    'restaurant.prebill_error': 'Failed to print pre-bill',
    'restaurant.split_order': 'Split Order',
    'restaurant.pay': 'Pay',
    'restaurant.split_items': 'Split Items',
    'restaurant.shared_items': 'Shared Items',
    'restaurant.no_split_items': 'No items to split',
    'restaurant.move': 'Move',
    'restaurant.create_split': 'Create Split',
    'restaurant.payment': 'Payment',
    'restaurant.payment_methods': 'Select Payment Method',
    'restaurant.cash': 'Cash',
    'restaurant.card': 'Card',
    'restaurant.complete_payment': 'Complete Payment',
    'restaurant.customer_name': 'Customer Name',
    'restaurant.close_order': 'Close Order',
    'restaurant.split_order_description': 'Select items to split between tables',
    'restaurant.split': 'Split',
    'restaurant.select_table_first': 'Please select a table first',
    'restaurant.table_added': 'Table added successfully',
    'restaurant.table_removed': 'Table removed successfully',
    'restaurant.table_number': 'Table Number',
    'restaurant.table_capacity': 'Table Capacity',
    'restaurant.table_number_required': 'Table number is required',
    'restaurant.printing_all_tables': 'Printing all table information',
    'restaurant.kitchen_view_opened': 'Kitchen view opened',
    'restaurant.free_table': 'Free Table',
    'restaurant.time': 'Time',
    'restaurant.reserve_table': 'Reserve Table',
    'restaurant.reopen_last_tab': 'Reopen last closed tab',
    'restaurant.new_reservation': 'New Reservation',
    'restaurant.waitlist_hint': 'No table selected — you can assign a table later when one becomes available.',
    'restaurant.create_reservation': 'Create Reservation',
    'restaurant.customer_phone': 'Phone Number',
    'restaurant.customer_email': 'Email',
    'restaurant.reservation_time': 'Reservation Time',
    'restaurant.party_size': 'Party Size',
    'restaurant.special_requests': 'Special Requests',
    'restaurant.reservation_details': 'Reservation Details',
    'restaurant.confirm_arrival': 'Confirm Arrival',
    'restaurant.start_order': 'Start Order',
    'restaurant.reserved_table': 'Reserved Table',
    'restaurant.click_to_view_details': 'Click to view reservation details',
    'restaurant.view_reservation': 'View Reservation',
    'restaurant.delete_all_warning': 'WARNING: This will delete ALL tables from Firebase!\n\nThis action cannot be undone!\n\nAre you absolutely sure?',
    'restaurant.delete_all_success': 'Successfully deleted {count} tables!\n\nPlease refresh the page.',
    'restaurant.delete_all_error': 'Error',
    'restaurant.customer_name_placeholder': 'Customer Name',
    'restaurant.phone_placeholder': 'Phone Number (e.g., +49 123 456789)',
    'restaurant.email_placeholder': 'Email Address',
    'restaurant.party_size_placeholder': 'Party Size',
    'restaurant.special_requests_placeholder': 'Special Requests',
    'restaurant.today_reservations': "Today's Reservations",
    'restaurant.future_reservations': 'Future Reservations',
    'restaurant.past_reservations': 'Past Reservations',
    'restaurant.current_future_reservations': 'Current & Future Reservations',
    'restaurant.upcoming_reservations': 'Upcoming Reservations!',
    'restaurant.tomorrow_reservations': "Tomorrow's Reservations",
    'restaurant.reservation': 'Reservation',
    'restaurant.reservations': 'Reservations',
    'restaurant.in_next_2_hours': 'in next 2 hours',
    'restaurant.for_today': 'for today',
    'restaurant.for_tomorrow': 'for tomorrow',
    'restaurant.dismiss': 'Dismiss',
    'restaurant.view_all_reservations': 'View All Reservations',
    'restaurant.view_details': 'View Details',
    'restaurant.assign_table': 'Assign Table',
    'restaurant.no_table_waitlist': 'No Table (Waitlist)',
    'restaurant.no_available_tables': 'No available tables right now',
    'restaurant.select_table_for': 'Select an available table for this reservation',
    'restaurant.cancel_reservation': 'Cancel Reservation',
    'restaurant.cancel': 'Cancel',
    'restaurant.close': 'Close',
    'restaurant.area': 'Area',
    'restaurant.phone': 'Phone',
    'restaurant.loading_orders': 'Loading orders...',
    'restaurant.table_order_details': 'Table {number} — Full Order Details',

    // Dashboard - Table Orders
    'dashboard.active_order_items': 'ACTIVE ORDER',
    'dashboard.previously_served': 'PREVIOUSLY SERVED',
    'dashboard.running_total': 'Running Total',
    'dashboard.no_menu_items': 'No menu items available',
    'dashboard.no_items_in': 'No items found in',
    'dashboard.show_all_items': 'Show All Items',

    // Login Page
    'login.pos_system': 'POINT OF SALE SYSTEM',
    'login.welcome_back': 'Welcome Back',
    'login.sign_in_subtitle': 'Sign in to access your dashboard',
    'login.email': 'Email',
    'login.email_placeholder': 'Enter your email',
    'login.password': 'Password',
    'login.password_placeholder': 'Enter your password',
    'login.remember_me': 'Remember me',
    'login.signing_in': 'Signing in...',
    'login.sign_in': 'Sign In',
    'login.error_empty_fields': 'Please enter both email and password',
    'login.error_network': 'Network error. Please try again.',
    'login.error_failed': 'Login failed',

    // Orders - Error/Alert messages
    'orders.error_load_failed': 'Failed to load orders',
    'orders.invoice_sent_success': 'Invoice sent to cashier printer successfully!',
    'orders.print_failed': 'Print failed',
    'orders.popup_blocked': 'Failed to open invoice window. Please allow popups for this site and try again.',
    'orders.invoice_failed': 'Failed to generate invoice',
    'orders.print_sent': 'Print job sent to cashier printer!',
    'orders.print_queued_warning': 'Print job queued but relay did not confirm. Check the cashier printer.',
    'orders.print_failed_generic': 'Print failed',
    'orders.order_not_found': 'Order not found',
    'orders.driver_confirmation_required': 'This order requires driver confirmation. Only dine-in and restaurant orders can be completed manually.',
    'orders.driver_manages_status': 'Cannot change order status. Driver is assigned and status is managed by the rider app.',
    'orders.complete_failed': 'Failed to complete order. Please try again.',
    'orders.payment_fixed': 'Payment status fixed! Order will now appear in Payments and Reports.',
    'orders.payment_fix_condition': 'Order must be delivered/completed with pending payment to fix.',
    'orders.payment_fix_failed': 'Failed to fix payment status',
    'orders.select_driver_first': 'Please select a driver first',
    'orders.driver_not_found': 'Driver not found',
    'orders.update_failed': 'Failed to update order status. Please try again.',
    'orders.edit_item': 'Edit item',
    'orders.search_placeholder': 'Search orders...',

    // Payments - Fallbacks and errors
    'payments.walk_in_customer': 'Walk-in Customer',
    'payments.unknown': 'Unknown',
    'payments.na': 'N/A',
    'payments.error_load_failed': 'Failed to load payments',
    'payments.refund_placeholder': 'Enter reason for refund...',

    // Dashboard - Error messages
    'dashboard.create_order_failed': 'Failed to create order. Please try again.',
    'dashboard.edit_item': 'Edit item',

    // Daily Closing - Additional alerts
    'daily_closing.export_error': 'Error exporting PDF file',
    'daily_closing.id_missing': 'Report ID is missing. Cannot delete.',
    'daily_closing.info_missing': 'Report information missing. Cannot reset.',
    'daily_closing.print_interim_success': 'Interim report printed successfully! ({success}/{total} printers)',
    'daily_closing.print_interim_error': 'Print error',
    'daily_closing.popup_blocked': 'Popup blocked. Please allow popups for this site.',

    // Edit Order Item
    'orders.load_addons_failed': 'Failed to load add-ons. Please try again.',
    'orders.save_changes_failed': 'Failed to save changes. Please try again.',
    'orders.additional_notes': 'Additional notes...',

    // Orders - Page header and stats
    'orders.subtitle': 'Manage and track all customer orders',
    'orders.stats_total': 'Total Orders',
    'orders.stats_active': 'Active Orders',
    'orders.stats_completed': 'Completed',
    'orders.stats_revenue': 'Revenue',

    // Orders - Filters
    'orders.filter_by': 'Filter by',
    'orders.filter_all': 'All Orders',
    'orders.filter_order_number': 'Order Number',
    'orders.filter_customer': 'Customer',
    'orders.filter_price': 'Price',
    'orders.filter_date': 'Date',
    'orders.clear_filter': 'Clear Filter',
    'orders.orders_found': 'orders found',

    // Orders - Types
    'orders.type': 'Type',
    'orders.type_delivery': 'Delivery',
    'orders.type_pickup': 'Pickup',
    'orders.type_dine_in': 'Dine-In',
    'orders.order_type': 'Order Type',

    // Orders - Table and split
    'orders.table': 'Table',
    'orders.split': 'Split',
    'orders.split_payment': 'Split Payment',

    // Orders - Actions
    'orders.view_order_details': 'View order details',
    'orders.confirm_order': 'Confirm order',
    'orders.complete_order': 'Complete order',
    'orders.cancel_order': 'Cancel order',
    'orders.no_orders_found': 'No orders found',
    'orders.try_different_filter': 'Try a different filter or search term',
    'orders.new_orders_appear': 'New orders will appear here automatically',
    'orders.no_order_selected': 'No order selected',

    // Orders - Customer detail fields
    'orders.edit': 'Edit',
    'orders.first_name': 'First Name',
    'orders.last_name': 'Last Name',
    'orders.customer_name': 'Customer Name',
    'orders.not_provided': 'Not provided',
    'orders.telephone': 'Telephone',
    'orders.address': 'Address',
    'orders.postcode': 'Postcode',
    'orders.city': 'City',
    'orders.delivery_address': 'Delivery Address',

    // Orders - Delivery & location
    'orders.delivery_location': 'Delivery Location',
    'orders.location_confirmed': 'Location Confirmed',
    'orders.not_confirmed': 'Not Confirmed',
    'orders.no_delivery_address': 'No delivery address provided',

    // Orders - Info and payment
    'orders.order_information': 'Order Information',
    'orders.status_and_payment': 'Status & Payment',
    'orders.created': 'Created',
    'orders.created_by': 'Created By',
    'orders.created_by_info': 'Created by system',
    'orders.addons': 'Add-ons',
    'orders.special_instructions': 'Special Instructions',
    'orders.fix_payment_status': 'Fix Payment Status',

    // Orders - Driver assignment
    'orders.driver_assigned': 'Driver assigned',
    'orders.assign_driver_failed': 'Failed to assign driver',

    // Orders - Edit customer dialog
    'orders.edit_customer_information': 'Edit Customer Information',
    'orders.enter_customer_name': 'Enter customer name',
    'orders.customer_address': 'Customer Address',
    'orders.enter_customer_address': 'Enter customer address',
    'orders.updating': 'Updating...',
    'orders.save': 'Save',

    // Checkout - Additional placeholders
    'checkout.first_name_placeholder': 'First Name',
    'checkout.last_name_placeholder': 'Last Name',
    'checkout.phone_placeholder': 'Phone Number (e.g., +49 123 456789)',
    'checkout.address_placeholder': 'Street Address',
    'checkout.postcode_placeholder': 'Enter postal code (e.g. 50354)',
    'checkout.city_placeholder': 'City',
    'checkout.customer_name_placeholder': 'Enter customer name...',
    'checkout.list_btn': 'List',
    'checkout.no_customers_found': 'No customers found',
    'checkout.table_number_helper': 'Required only for dine-in table orders',
    'checkout.table_occupied_message': 'This table has an active order. Please complete or close the existing order first, or choose a different table.',
    'checkout.table_reserved_message': 'This table is reserved. Please choose an available table or cancel the reservation first.',
    'checkout.choose_another_table': 'Choose Another Table',
    'checkout.adding_to_table': 'Adding to Table',
    'checkout.customer_list': 'Customer List',
    'checkout.add_customer': 'Add Customer',
    'checkout.orders_count': 'orders',
    'checkout.add_new_customer': 'Add New Customer',
    'checkout.first_name_label': 'First Name',
    'checkout.last_name_label': 'Last Name',
    'checkout.phone_label': 'Phone Number',
    'checkout.address_label': 'Address',
    'checkout.postcode_label': 'Postal Code',
    'checkout.city_label': 'City',
    'checkout.close_checkout': 'Close Checkout?',
    'checkout.unsaved_changes_message': 'There are unsaved changes. The order will be saved as a draft and can be resumed later.',
    'checkout.cancel_and_delete': 'Cancel & Delete',
    'checkout.saving': 'Saving...',
    'checkout.save_draft_and_close': 'Save as Draft & Close',
    'checkout.paypal_create_failed': 'Failed to create PayPal order',
    'checkout.payment_processing_failed': 'Payment processing failed',
    'checkout.save_draft_error': 'Error saving draft. Please try again.',
    'checkout.table_context_label': 'Table {number}',
    'checkout.restaurant_order': 'Restaurant Order',
    'checkout.paypal_qr_info': 'A QR code will be displayed after creating the order',
    'checkout.paypal_qr_desc': 'Customer will scan the QR code with PayPal app to pay',
    'checkout.confirm_delete_customer': 'Are you sure you want to delete "{name}"?',
    'checkout.name_or_phone_required': 'Please enter at least a name or phone number.',

    // Split - Additional alerts
    'split.shared_item_alert': 'Shared Item',
    'split.complete_error': 'Error completing split',
    'split.print_error': 'Print error',
    'split.distribute_equally': 'Distribute Equally',
    'split.items_assigned_count': 'items assigned',
    'split.select_person': 'Select person...',
    'split.no_items_available': 'No items available. Please add items to the cart first.',
    'split.person_summary': 'Summary per Person',
    'split.save_draft_error': 'Error saving draft. Please try again.',
    'split.shared_item_detail': '{name} (€{price}) has been split equally among {count} people. Each person pays €{each}.',
    'split.for_person': 'For: {name}',
    'split.split_table_header': 'SPLIT TABLE {table}',
    'split.split_between_people': 'Split between {count} people',
    'split.restaurant_table_service': 'Restaurant Table Service',
    'split.split_payment_label': 'Split Payment',
    'split.paid_status': 'Paid',
    'split.distributed_to_others': 'will be distributed to other people',
    'split.taken_from_others': 'taken from other people',
    'split.reduced_by': 'Reduced by',
    'split.increased_by': 'Increased by',
    'split.total_balance_missing': 'Total Balance: Missing €{amount}',
    'split.total_balance_excess': 'Total Balance: Excess €{amount}',
    'split.total_amount_balanced': 'Total amount balanced',
    'split.saving': 'Saving...',
    'split.people': 'people',
    'split.partial_pay': 'Partial Pay',
    'split.step': 'Step',
    'split.split_completed': 'Split Already Paid',
    'split.split_completed_hint': 'Release table to clear',
    'split.close_split': 'Close Split?',
    'split.close_split_message': 'There are unsaved changes. The split will be saved as a draft and can be resumed later.',
    'split.cancel_delete': 'Cancel & Delete',
    'split.save_draft_close': 'Save as Draft & Close',
    'split.special_instructions_header': 'SPLIT TABLE ORDER - {count} people sharing the bill',
    'split.split_bill': 'Split Bill',
    'split.add_people_hint': 'Add people to split the bill',
    'split.max_people_hint': 'Up to 8 people supported',
    'split.items_assigned_of': '{assigned} of {total} items assigned',
    'split.collect': 'Collect',
    'split.all_assigned': 'All assigned',
    'split.no_paid_guests': 'No Paid Guests',
    'split.no_paid_message': 'No paid people to checkout.',
    'split.partial_complete': 'Partial Checkout Complete',
    'split.partial_complete_message': '{names} paid and left. Items removed from bill. Table {table} remains open.',
    'split.partial_failed': 'Checkout Failed',
    'split.split_receipt': 'Split Receipt (TSE)',
    'split.kitchen_order': 'Kitchen Order',
    'split.export_csv': 'Export CSV',
    'split.missing_guests': 'Missing Guests',
    'split.no_items_assigned_title': 'No Items Assigned',
    'split.payment_required': 'Payment Required',
    'split.name_required': 'Name Required',

    // Add-ons / Edit Item
    'addons.edit_item': 'Edit Item',
    'addons.add_item_to_cart': 'Add Item to Cart',
    'addons.select_addons': 'Select Add-ons',
    'addons.each': 'each',
    'addons.no_addons_available': 'No add-ons available',
    'addons.selected_addons': 'Selected Add-ons',
    'addons.total_addons': 'Total Add-ons',
    'addons.notes_optional': 'Notes (optional)',
    'addons.item_label': 'Item',
    'addons.addons_label': 'Add-ons',
    'addons.save_changes': 'Save Changes',
    'addons.add_to_cart': 'Add to Cart',
    'addons.optional': 'Optional',
    'addons.quantity': 'Quantity',
    'addons.price': 'Price',
    'addons.addons_extras': 'Add-ons / Extras',
    'addons.no_addons_for_item': 'No add-ons available for this item',
    'addons.notes_comments': 'Notes / Comments',
    'modifiers.optional': 'Optional',
    'modifiers.required': 'Required',
    'modifiers.choose_required': 'Please select required options',

    // Sidebar
    'sidebar.current_language': 'English',

    // Dashboard - Draft section
    'dashboard.drafts_pending_split': 'Draft & Pending Split Orders',
    'dashboard.step': 'Step',
    'dashboard.people': 'People',
    'dashboard.confirm_delete_draft_table': 'Are you sure you want to delete the draft for Table {table}?',
    'dashboard.confirm_delete_draft': 'Are you sure you want to delete this draft?',

    // Dashboard - Draft errors
    'dashboard.delete_draft_error': 'Error deleting draft. Please try again.',

    // Dashboard - Table session
    'dashboard.table': 'Table',
    'dashboard.clear_table': 'Clear Table',
    'dashboard.active_tables': 'Active Tables',
    'dashboard.previous_orders': 'previous orders',
    'dashboard.items_ordered': 'items ordered',
    'dashboard.previous_table_orders': 'Previous Table Orders',
    // Add-Items (edit-order) banner
    'add_items.banner_title': '+ Adding items to order #{number}',
    'add_items.banner_subtitle': '{customer} · {count} items · €{total} · new items will print as addition',
    'add_items.show_items': 'Show items',
    'add_items.hide_items': 'Hide items',
    'add_items.cancel': 'Cancel',
    'add_items.close': 'Close',
    'add_items.already_in_order': 'Already in order #{number}',
    'add_items.running_total': 'Running total',
    'orders.add_items': '+ Add Items',
    'orders.add_items_tooltip': 'Add more items to this order',
    // Manager PIN approval dialog (refund / cancel / void / big discount / reopen tab)
    'manager_pin.title': 'Manager Approval Required',
    'manager_pin.reason_label': 'Reason (required)',
    'manager_pin.reason_placeholder': 'e.g. Customer requested refund',
    'manager_pin.reason_required': 'Reason is required — type why you are authorizing this',
    'manager_pin.enter_pin': 'Enter Manager PIN',
    'manager_pin.invalid': 'Invalid PIN. Ask an admin to set your PIN in Admin → Users → 🛡 icon, or use the master PIN.',
    'manager_pin.verify_failed': 'Verification failed. Please try again.',
    'manager_pin.verifying': 'Verifying...',
    'manager_pin.authorize_note': 'Only managers and admins can authorize this action.',
    'manager_pin.cancel': 'Cancel',
    'manager_pin.approve': 'Approve',
    // Opening cash dialog (shift start)
    'open_shift.title': 'Open Shift — Starting Cash',
    'open_shift.subtitle': 'Count the drawer before you take orders',
    'open_shift.description': "How much cash is in the drawer right now? This is your opening float — it's used to calculate the variance at the end of your shift.",
    'open_shift.tip': "Tip: most shops start with €100–€200. If you don't know, enter 0 — any variance at close will simply reflect the cash you made during the shift.",
    'open_shift.skip': 'Skip',
    'open_shift.start_shift': 'Start Shift',
    'open_shift.saving': 'Saving…',
    'open_shift.invalid_amount': 'Enter a valid amount (0 or more)',
    'open_shift.save_failed': 'Failed to save opening cash',

    // Draft Split Orders Dialog
    'drafts.title': 'Draft & Pending Split Orders',
    'drafts.loading': 'Loading drafts...',
    'drafts.load_error': 'Error loading drafts',
    'drafts.no_drafts': 'No drafts found',
    'drafts.no_drafts_desc': 'All split orders have been completed or there are no saved drafts.',
    'drafts.table': 'Table',
    'drafts.step': 'Step',
    'drafts.people': 'People',
    'drafts.total_amount': 'Total Amount',
    'drafts.created_by': 'Created by',
    'drafts.order_id': 'Order ID',
    'drafts.people_label': 'People:',
    'drafts.delete': 'Delete',
    'drafts.resume': 'Resume',
    'drafts.refresh': 'Refresh',
    'drafts.confirm_delete': 'Are you sure you want to delete this draft?',
    'drafts.delete_error': 'Error deleting draft',
    'drafts.status_draft': 'Draft',
    'drafts.status_pending': 'Pending',
    'drafts.step_create': 'Create Split',
    'drafts.step_assign': 'Assign Items',
    'drafts.step_payment': 'Process Payments',
    'drafts.step_complete': 'Complete',

    // Driver map
    'drivers.location_load_failed': 'Failed to load driver location',

    // Driver screen
    'drivers.all_orders': 'All orders',
    'drivers.unbooked_orders': 'Unbooked orders',
    'drivers.active_orders': 'Active orders',
    'drivers.completed_label': 'Completed',
    'drivers.filter_all': 'ALL',
    'drivers.filter_new': 'New ({count})',
    'drivers.filter_active': 'Active',
    'drivers.filter_done': 'Done',
    'drivers.nr': 'Nr',
    'drivers.time': 'Time',
    'drivers.customer_platform_sum': 'Customer, Platform, Sum',
    'drivers.order_type': 'Order type',
    'drivers.status': 'Status',
    'drivers.no_delivery_orders': 'No delivery orders',
    'drivers.orders_appear_here': 'Delivery orders will appear here automatically',
    'drivers.orders_count': '{count} Orders',
    'drivers.on_map': '{count} on Map',
    'drivers.drivers_online': '{count} Drivers Online',
    'drivers.drivers_label': 'Drivers ({online}/{total})',
    'drivers.book_on_driver': 'Book on Driver',
    'drivers.assign_driver_to_order': 'Assign driver to order',
    'drivers.no_drivers_available': 'No drivers available',
    'drivers.add_drivers_hint': 'Add drivers from the Admin panel',
    'drivers.no_drivers_registered': 'No drivers registered',
    'drivers.show_order': 'Show order',
    'drivers.open_google_maps': 'Open in Google Maps',
    'drivers.print': 'Print',
    'drivers.complete_order': 'Complete order',
    'drivers.cancel_order': 'Cancel order',
    'drivers.google_maps': 'Google Maps',
    'drivers.close': 'Close',
    'drivers.order_number': 'Order #{number}',
    'drivers.items_count': 'Items ({count})',
    'drivers.total': 'Total',
    'drivers.customer': 'Customer',
    'drivers.assigned_driver': 'Assigned Driver',
    'drivers.delivery': 'Delivery',
    'drivers.status_new': 'New',
    'drivers.status_confirmed': 'Confirmed',
    'drivers.status_assigned': 'Assigned',
    'drivers.status_picked_up': 'Picked Up',
    'drivers.status_on_the_way': 'On the way',
    'drivers.status_completed': 'Completed',
    'drivers.status_cancelled': 'Cancelled',
    'drivers.legend_restaurant': 'Restaurant',
    'drivers.legend_new_order': 'New Order',
    'drivers.legend_driver': 'Driver',
    'drivers.legend_completed': 'Completed',
    'drivers.today': 'today',
    'drivers.active': 'active',
    'drivers.online': 'Online',
    'drivers.offline': 'Offline',

    // Kitchen Display Screen
    'kitchen.title': 'Kitchen',
    'kitchen.all_stations': 'All Stations',
    'kitchen.loading': 'Loading orders...',
    'kitchen.all_done': 'All caught up!',
    'kitchen.no_orders_hint': 'New orders will appear here automatically',
    'kitchen.new': 'New',
    'kitchen.confirmed': 'Confirmed',
    'kitchen.preparing': 'Preparing',
    'kitchen.ready': 'Ready',
    'kitchen.all_ready': 'All ready',
    'kitchen.in_progress': 'In Progress',
    'kitchen.overdue': 'Overdue!',
    'kitchen.left': 'left',
    'kitchen.items': 'items',
    'kitchen.table': 'Table',
    'kitchen.dine_in': 'Dine In',
    'kitchen.takeaway': 'Takeaway',
    'kitchen.delivery': 'Delivery',
    'kitchen.start_preparing': 'Start',
    'kitchen.mark_ready': 'Ready',
    'kitchen.done': 'Done',
    'kitchen.confirm_complete': 'Complete order №{number}?',
    'kitchen.confirm_complete_desc': 'This will remove the order from the kitchen screen and mark it as completed.',
    'kitchen.cancel': 'Cancel',
    'kitchen.confirm': 'Complete',

    // Common additional
    'common.comment_placeholder': 'Comment to menu...',

    // Delivery Map
    'map.delivery_map_distance': 'Delivery Map & Distance',
    'map.api_key_required': 'Google Maps API key required',
    'map.distance': 'Distance:',
    'map.estimated_drive_time': 'Estimated Drive Time:',
    'map.minutes': 'minutes',
    'map.loading_coordinates': 'Loading coordinates...',
    'map.delivery_address': 'Delivery Address',
    'map.calculating_route': 'Calculating route...',
    'map.map_tip': 'Tip: Click on the map to open it in Google Maps',

    // PayPal QR Payment
    'paypal.qr_payment': 'PayPal QR Payment',
    'paypal.waiting_for_payment': 'Waiting for payment...',
    'paypal.scan_qr_code': 'Please scan the QR code with your PayPal app',
    'paypal.payment_successful': 'Payment Successful!',
    'paypal.payment_completed_message': 'Payment has been completed successfully',
    'paypal.payment_failed': 'Payment Failed',
    'paypal.payment_failed_message': 'Payment could not be completed',
    'paypal.scan_qr_complete': 'Scan this QR code with your PayPal app to complete the payment',
    'paypal.amount_to_pay': 'Amount to pay',
    'paypal.time_remaining': 'Time remaining:',
    'paypal.open_in_browser': 'Open in Browser',
    'paypal.cancel_order': 'Cancel Order',
    'paypal.monitoring_status': 'Monitoring payment status...',

    // Payments - Split
    'payments.split_label': 'Split',
    'payments.split_payment_info': 'Split Payment',
    'payments.part_of_split_bill': 'Part of split bill',

    // Reports - Split
    'reports.split_payments': 'Split Payments',

    // Daily Closing - Employee performance section
    'daily_closing.subtitle': 'Daily reports & cash register closing',
    'daily_closing.interim_format_description': 'Generate and print in German cash register format',
    'daily_closing.generate_preview': 'Generate & Preview',
    'daily_closing.print_direct_description': 'Print latest report directly to ESC/POS printer',
    'daily_closing.reports_count': 'reports',
    'daily_closing.employee_performance': 'Employee Performance',
    'daily_closing.employee_label': 'Employee',
    'daily_closing.sales_label': 'Sales',
    'daily_closing.hours_label': 'Hours',
    'daily_closing.avg_order': 'Avg Order',
    'daily_closing.work_period': 'Work Period',

    // Restaurant - Additional labels
    'restaurant.reservations_label': 'Reservations',
    'restaurant.drafts_label': 'Drafts',
    'restaurant.refresh': 'Refresh',
    'restaurant.loading_tables': 'Loading tables...',
    'restaurant.seats': 'seats',
    'restaurant.customer_label': 'Customer',
    'restaurant.rounds': 'rounds',
    'restaurant.add_order_reorder': '+ Add Order',
    'restaurant.view_all_orders': 'View All Orders',
    'restaurant.close_tab_charge': 'Close Tab & Charge',

    // Settings Pages
    'settings.coming_soon': 'Coming soon',
    'settings.back': 'Back',
    'settings.save': 'Save',
    'settings.saving': 'Saving...',
    'settings.cancel': 'Cancel',
    'settings.delete': 'Delete',
    'settings.goods_menu': 'Goods / menu / settings',
    'settings.delivery_settings': 'Delivery settings',
    'settings.cash_desk_settings': 'Cash desk settings',
    'settings.restaurant_settings': 'Restaurant settings',
    'settings.cash_book_settings': 'Cash book / Settings',
    'settings.program_settings': 'Program settings',
    'settings.datev': 'DateV',
    'settings.marketing': 'Marketing',
    'settings.payment_methods': 'Payment methods',
    'settings.webshop': 'WebShop',
    'settings.kitchen_monitor': 'Kitchen monitor',
    'settings.add_desktop_client': 'Add desktop client',
    'settings.printer_settings': 'Printer settings',
    'settings.phone_settings': 'Phone settings',
    'settings.user_permissions': 'User / permissions',
    'settings.api_interfaces': 'API / Interfaces / Order Smart',
    'settings.tax_settings': 'MwSt / tax settings',
    'settings.general_settings': 'Settings',
    'settings.client_monitor': 'Setting up the client monitor',
    'settings.permissions': 'Permissions',
    'settings.user_management': 'User management',
    'settings.access_settings': 'Access settings',
    'settings.page_section': 'Page / Section',
    'settings.access': 'Access',
    'settings.not_visible': 'Not visible',
    'settings.permissions_saved': 'Permissions saved successfully',
    'settings.permissions_save_error': 'Error saving permissions',
    'settings.role_owner': 'Owner',
    'settings.role_restaurant_admin': 'Restaurant admin',
    'settings.role_admin': 'Admin',
    'settings.role_cashier': 'Cashier',
    'settings.role_waiter': 'Waiter',
    'settings.role_cook': 'Cook',
    'settings.role_driver': 'Driver',
    'settings.page_home': 'Home',
    'settings.page_all_orders': 'All orders',
    'settings.page_delivery': 'Delivery service',
    'settings.page_cash_desk': 'Cash desk',
    'settings.page_restaurant': 'Restaurant',
    'settings.page_kitchen': 'Kitchen screen',
    'settings.page_driver': 'Driver screen',
    'settings.page_reports': 'Reports',
    'settings.page_backoffice': 'Backoffice',
    'settings.page_settings': 'Settings',
    'settings.name': 'Name',
    'settings.email': 'Email',
    'settings.role': 'Role',
    'settings.status': 'Status',
    'settings.actions': 'Actions',
    'settings.active': 'Active',
    'settings.inactive': 'Inactive',
    'settings.pin': 'PIN (optional)',
    'settings.add_user': 'Add User',
    'settings.edit_user': 'Edit User',
    'settings.no_users': 'No users yet. Click "Add User" to create one.',
    'settings.fill_required_fields': 'Please fill all required fields',
    'settings.user_created': 'User created successfully',
    'settings.user_updated': 'User updated successfully',
    'settings.user_save_error': 'Error saving user',
    'settings.user_deleted': 'User deleted successfully',
    'settings.user_delete_error': 'Error deleting user',
    'settings.confirm_delete': 'Confirm Delete',
    'settings.delete_user_confirm': 'Are you sure you want to delete this user?',
  },
  ar: {
    // High-priority Arabic translations for the user-visible flows.
    // Any key NOT present here falls back to the English string automatically
    // (see `t()` below). Translate more keys as we polish the Arabic UI.

    // Navigation
    'nav.dashboard': 'اللوحة الرئيسية',
    'nav.restaurant': 'المطعم',
    'nav.orders': 'الطلبات',
    'nav.delivery': 'التوصيل',
    'nav.tables': 'الطاولات',
    'nav.kitchen': 'المطبخ',
    'nav.drivers': 'السائقون',
    'nav.backoffice': 'الإدارة',
    'nav.reports': 'التقارير',
    'nav.payments': 'المدفوعات',
    'nav.settings': 'الإعدادات',
    'nav.logout': 'تسجيل الخروج',

    // Common
    'common.welcome': 'أهلاً بك',
    'common.login': 'تسجيل الدخول',
    'common.logout': 'تسجيل الخروج',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.add': 'إضافة',
    'common.search': 'بحث',
    'common.loading': 'جارٍ التحميل…',
    'common.error': 'خطأ',
    'common.success': 'تم بنجاح',
    'common.confirm': 'تأكيد',
    'common.yes': 'نعم',
    'common.no': 'لا',
    'common.back': 'رجوع',
    'common.next': 'التالي',
    'common.close': 'إغلاق',
    'common.actions': 'الإجراءات',

    // Login
    'login.pos_system': 'نظام نقاط البيع',
    'login.welcome_back': 'مرحبًا بعودتك',
    'login.sign_in_subtitle': 'سجّل دخولك للوصول إلى لوحتك',
    'login.email': 'البريد الإلكتروني',
    'login.email_placeholder': 'name@restaurant.com',
    'login.password': 'كلمة المرور',
    'login.password_placeholder': 'كلمة المرور',
    'login.remember_me': 'تذكرني',
    'login.sign_in': 'تسجيل الدخول',
    'login.signing_in': 'جارٍ تسجيل الدخول…',
    'login.error_empty_fields': 'يرجى إدخال البريد الإلكتروني وكلمة المرور.',
    'login.error_failed': 'تعذّر تسجيل الدخول. تحقّق من بياناتك.',
    'login.error_network': 'مشكلة في الاتصال. حاول مرة أخرى.',

    // Orders
    'orders.title': 'الطلبات',
    'orders.new_order': 'طلب جديد',
    'orders.order_number': 'رقم الطلب',
    'orders.customer': 'العميل',
    'orders.total': 'الإجمالي',
    'orders.status': 'الحالة',
    'orders.pending': 'قيد الانتظار',
    'orders.confirmed': 'مؤكَّد',
    'orders.preparing': 'قيد التحضير',
    'orders.ready': 'جاهز',
    'orders.delivered': 'تم التوصيل',
    'orders.cancelled': 'مُلغى',
    'orders.online_order': 'طلب أونلاين',
    'orders.walkin_order': 'طلب داخل المطعم',
    'orders.print_sent': 'تم إرسال أمر الطباعة.',
    'orders.print_queued_warning': 'تم وضع الطلب في قائمة الانتظار للطباعة.',
    'orders.print_failed_generic': 'فشل الطباعة',

    // Dashboard
    'dashboard.create_new_order': 'إنشاء طلب جديد',
    'dashboard.discover_subtitle': 'تصفّح القائمة واصنع تجربة طعام مميزة',
    'dashboard.search_menu': 'ابحث في القائمة…',
    'dashboard.current_order': 'الطلب الحالي',
    'dashboard.cart_empty': 'السلة فارغة',
    'dashboard.add_items_hint': 'أضِف أصنافًا من القائمة للبدء',
    'dashboard.subtotal': 'المجموع الفرعي',
    'dashboard.tax': 'الضريبة',
    'dashboard.total': 'الإجمالي',
    'dashboard.checkout': 'الدفع',
    'dashboard.split_table': 'تقسيم الطاولة',
    'dashboard.clear_cart': 'تفريغ السلة',
    'dashboard.no_menu_items': 'لا توجد أصناف متاحة',
    'dashboard.upload_menu_hint': 'يُرجى رفع أصناف القائمة إلى قاعدة البيانات',
    'dashboard.all': 'الكل',

    // Payment
    'payment.cash': 'نقدًا',
    'payment.card': 'بطاقة',
    'payment.paypal': 'PayPal',
    'payment.split': 'دفع مُقسَّم',
    'payment.amount': 'المبلغ',
    'payment.change': 'الباقي',
    'payment.total': 'الإجمالي',
    'payment.subtotal': 'المجموع الفرعي',
    'payment.tax': 'ضريبة القيمة المضافة',
    'payment.delivery_fee': 'رسوم التوصيل',

    // Menu
    'menu.sold_out_badge': 'نفد',
    'menu.sold_out': 'نفد',
    'menu.categories': 'الفئات',
    'menu.items': 'الأصناف',
    'menu.price': 'السعر',
    'menu.available': 'متوفر',
    'menu.unavailable': 'غير متوفر',
    'menu.add_to_cart': 'أضِف إلى السلة',

    // Add-ons modal
    'addons.add_item_to_cart': 'إضافة صنف إلى السلة',
    'addons.edit_item': 'تعديل الصنف',
    'addons.add_to_cart': 'أضِف إلى السلة',
    'addons.save_changes': 'حفظ التغييرات',
    'addons.select_addons': 'اختر الإضافات',
    'addons.addons_label': 'الإضافات',
    'addons.no_addons_available': 'لا توجد إضافات متاحة',
    'addons.notes_optional': 'ملاحظات (اختياري)',
    'addons.notes_placeholder': 'طلبات خاصة، حساسية، تعديلات…',
    'addons.item_label': 'الصنف',
    'addons.optional': 'اختياري',
    'addons.each': 'للوحدة',
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // English-default. Arabic is the secondary locale. Any stale 'de' value
  // from a pre-rebrand deploy is migrated back to 'en'.
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem('saheeh_pos_language');
      if (stored === 'ar') return 'ar';
      localStorage.setItem('saheeh_pos_language', 'en');
    } catch { /* ignore */ }
    return 'en';
  });

  // Save language preference + flip document direction for RTL.
  useEffect(() => {
    try { localStorage.setItem('saheeh_pos_language', language); } catch { /* ignore */ }
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key: string, params?: Record<string, string | number>): string => {
    const dict      = translations[language] as Record<string, string>;
    const enDict    = translations.en as Record<string, string>;
    let translation = dict[key] ?? enDict[key] ?? key;

    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(`{${paramKey}}`, String(value));
      });
    }
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
